import { Link } from "react-router";
import { Section } from "../../components/Section";
import { AGE_GATE, GAMBLING_HELP } from "../../content/compliance";
import styles from "./Refusals.module.css";

/**
 * The two ways KEV refuses an account, rendered once.
 *
 * Both /signup (before the account exists) and /welcome (after it does, for
 * admin-created accounts and terms-version bumps) can hit either refusal, and
 * the server applies the identical rule to both — kev_eligibility is one module
 * for exactly that reason. The copy has to travel the same way, or the same
 * decision starts being explained two different ways depending on which door
 * the person came through.
 *
 * ## Neither of these is an error state, and neither may look like one
 *
 * They are ANSWERS. A red alert box says "you did something wrong, try again",
 * and the retry it invites is one the server will refuse every single time —
 * the rule is about who the person is, not what they typed. So: no `role`
 * `alert`, no `--drift` border, no raw server code on screen.
 *
 * ## Both state that nothing was kept, because nothing was
 *
 * The server writes no row, no attribute and no account when it refuses. Saying
 * so is not reassurance for its own sake: someone told they cannot have an
 * account is entitled to know we did not keep the date of birth they just gave
 * us in order to remember why.
 */

/**
 * Under 18.
 *
 * Carries the Gambling Help Online number. A person refused by an age gate on a
 * betting-analytics product is squarely in the population §3.1 exists for, and
 * this is the last screen we get to show them.
 */
export function UnderAgeRefusal() {
  return (
    <Section eyebrow="Sorry" heading={`${AGE_GATE} only`} tone="ink" level={1}>
      <div className={styles.wrap}>
        <p className={styles.lead}>
          KEV is an {AGE_GATE} product. That&rsquo;s not a judgement — it&rsquo;s the rule, and
          it doesn&rsquo;t bend for fantasy either.
        </p>
        <p className={styles.note}>
          Nothing has been saved. If you or someone you know needs support:{" "}
          {GAMBLING_HELP.name}{" "}
          <a href={`tel:${GAMBLING_HELP.phone.replace(/\s/g, "")}`}>{GAMBLING_HELP.phone}</a>.
        </p>
      </div>
    </Section>
  );
}

/**
 * Resident outside Australia.
 *
 * NO "coming soon to your region", and nothing that could be read as one. That
 * is a representation about future conduct; this is a licensing and
 * consumer-law position, not a roadmap. Unavailable.tsx carries the same rule
 * for the geo-blocked case and says so in its header — the difference is that
 * page answers "where are you", this one answers "where do you live".
 *
 * The legal documents stay linked. Someone refused service is exactly who has a
 * reason to read the terms.
 */
export function OffshoreRefusal() {
  return (
    <Section eyebrow="Sorry" heading="Australia only" tone="ink" level={1}>
      <div className={styles.wrap}>
        <p className={styles.lead}>
          KEV is available to Australian residents only. That&rsquo;s a licensing and
          consumer-law position — the terms are written under NSW law — and it isn&rsquo;t
          something we can make an exception on.
        </p>
        <p className={styles.note}>
          Nothing has been saved. You can still read the{" "}
          <Link to="/legal/terms">Terms</Link> and{" "}
          <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>
      </div>
    </Section>
  );
}
